import { useTranslation } from 'react-i18next';

import type { DashboardHealth, DashboardPeriod } from '@/api/dashboard';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { Glued, type Unit } from './Glued';

interface Props {
  health: DashboardHealth;
  period: DashboardPeriod;
}

/**
 * Whether a metric is better when it **falls** or when it **rises** — §6.2's *Better when it* column,
 * held here beside the figure's own definition.
 *
 * ⚠️ **This is what decides the delta's colour, and the sign is not** (§6.2, 2026-08-12, TASK-152). A
 * *fall* in median turnaround is the single best sign the module is working and renders `positive`; a
 * fall in the other two renders `negative`. The screen coloured by sign alone until today, so an
 * improvement on the one figure this section calls its best measure was drawn in red. It is a declared
 * property so that a fourth metric declares its own instead of being caught by a
 * `key === 'medianTurnaround'` branch buried in the JSX.
 */
type Polarity = 'falls' | 'rises';

/**
 * `DESIGN.md` §6.2, Section 2 — Health of the feedback loop.
 *
 * Three figures in one band, **second on the screen**, separated by vertical hairlines and not boxed in
 * a `border-y` frame — the reference's own treatment, and the difference between three figures that
 * read as one band and three that read as a card. All three are **metadata** — timing and counts — so
 * the section is not blinded and is the same for every viewer entitled to it (ADR 0015 §3). The
 * submission-rate denominator counts drafts **for visibility**, which ADR 0015 §4 reconciles with ADR
 * 0007: no draft's contents enter any of these numbers.
 *
 * ⚠️ **The unit is glued to the figure — `38h`, `24`, `82%`** (§6.2, 2026-08-12): one visual value
 * rather than a number followed by the word *hours* or *percent*. The glue is **visual, not semantic** —
 * `h` read aloud is a letter — so the accessible name still says the words, which is why each figure
 * renders the glued form `aria-hidden` beside an `sr-only` sentence. ⚠️ **`Glued` lived in this file
 * and moved to `Glued.tsx` on 2026-09-28** (BUG-092): Section 5's *Median turnaround* is the same
 * quantity in the same unit and rendered it bare, so the rule now has one implementation and two
 * callers rather than two grammars for one figure.
 *
 * ⚠️ **The figures are back at `type.display`, and that is `re-decided` rather than corrected** (§6.2,
 * 2026-08-12, TASK-148; TASK-129's argument was put to the user and they chose the original design).
 * TASK-129 took them out of *Display* because a real median of **0** at 48 px reads as breakage, and
 * that objection is **answered rather than dismissed**: with the unit glued the figure a reader meets at
 * a quiet moment is `0h`, which reads as a measurement. §3 gains no new type role for this screen — of
 * its two existing numeral roles, *Display* (48 px) is the one that is larger than the 28 px that
 * shipped, which is what the reference shows.
 *
 * A `null` value is an em dash, never a `0` — the API sends `null` when there is nothing to measure,
 * and a zero would be read as a measurement.
 *
 * **The delta always states its comparison in words** ("vs previous 30 days"): an arrow with no
 * baseline is decoration. For `All time` there is no prior window and the API sends `delta: null`,
 * which is said in words rather than drawn as a flat arrow.
 */
export function HealthSection({ health, period }: Props) {
  const { t } = useTranslation(['dashboard', 'common']);

  const figures: Array<{
    key: string;
    label: string;
    value: number | null;
    delta: number | null;
    unit: Unit;
    betterWhen: Polarity;
  }> = [
    {
      key: 'medianTurnaround',
      label: t('dashboard:health.medianTurnaround'),
      value: health.median_turnaround.value,
      delta: health.median_turnaround.delta,
      unit: 'hours',
      betterWhen: 'falls',
    },
    {
      key: 'submitted',
      label: t('dashboard:health.submitted'),
      value: health.submitted_this_period.value,
      delta: health.submitted_this_period.delta,
      unit: null,
      betterWhen: 'rises',
    },
    {
      key: 'submissionRate',
      label: t('dashboard:health.submissionRate'),
      value: health.submission_rate.value,
      delta: health.submission_rate.delta,
      unit: 'percent',
      betterWhen: 'rises',
    },
  ];

  return (
    <section className="mt-10" aria-label={t('dashboard:health.title')}>
      <Eyebrow>{t('dashboard:health.title')}</Eyebrow>

      {/*
        ⚠️ **A band separated by hairlines, not a boxed group** (§6.2, 2026-08-12). The `border-y` frame
        around the three drew a card; the reference draws three columns of one band, which is what the
        vertical rules between them say and what the missing box no longer contradicts.
      */}
      <dl className="grid grid-cols-1 lg:grid-cols-3">
        {figures.map((figure) => (
          <div
            key={figure.key}
            className="border-b border-rule py-4 pr-4 last:border-b-0 lg:border-b-0 lg:border-r lg:pl-4 lg:first:pl-0 lg:last:border-r-0"
          >
            <dt className={type.metadata}>{figure.label}</dt>
            <dd className="mt-2">
              <span className={type.display}>
                <Glued value={figure.value} unit={figure.unit} />
              </span>
              <p className="mt-2 flex items-baseline gap-2">
                {figure.delta === null ? (
                  <span className={cn(type.metadata, 'text-ink-500')}>
                    {t('dashboard:period.noComparison')}
                  </span>
                ) : (
                  <>
                    <span
                      className={cn(
                        type.numeral,
                        'text-xs',
                        deltaTone(figure.delta, figure.betterWhen),
                      )}
                    >
                      {/*
                        The **arrow follows the sign** while the colour follows the polarity, so the
                        direction and the judgement are two separate marks and neither has to carry
                        both (§6.2). `▼ 4h` on median turnaround is a fall drawn in `positive`.
                      */}
                      <span aria-hidden>
                        {figure.delta > 0 ? '▲' : figure.delta < 0 ? '▼' : '·'}
                      </span>{' '}
                      <span className="sr-only">
                        {t(
                          figure.delta > 0
                            ? 'dashboard:period.up'
                            : figure.delta < 0
                              ? 'dashboard:period.down'
                              : 'dashboard:period.unchanged',
                        )}{' '}
                      </span>
                      <Glued value={Math.abs(figure.delta)} unit={figure.unit} />
                    </span>
                    <span className={type.metadata}>
                      {t(`dashboard:period.comparison.${period}`)}
                    </span>
                  </>
                )}
              </p>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * §6.2's polarity rule as one function: an **improvement** is `positive`, a **regression** is
 * `negative`, and no change is neither.
 *
 * ⚠️ It read `delta > 0 && 'text-positive'` until 2026-08-12 — colour by sign alone — which rendered a
 * falling median turnaround, the single best sign the module is working, in red.
 */
function deltaTone(delta: number, betterWhen: Polarity): string {
  if (delta === 0) return 'text-ink-500';
  const improved = betterWhen === 'falls' ? delta < 0 : delta > 0;
  return improved ? 'text-positive' : 'text-negative';
}
